import React, { useContext, useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { Button, Checkbox, Divider, Typography, notification } from 'antd'

import { FlexColumn, FlexRow } from '../../FlexboxReusables/FlexItems'
import { ParticipantCourseContext } from '../Context/ParticipantCourseContext'
import { imageUrl } from '../../../globals'
import ParticipantNavbar from '../../Navigation/ParticipantNavbar'
import TimeIsUpModal from '../DisplayCourseMaterial/TimeIsUpModal'

enum ImageType {
  QUESTION = 'question',
  ANSWER = 'answer',
}

type ExamProps = {
  examAttemptId?: string | undefined
}

const Exam: React.FC<ExamProps> = () => {
  const examAttemptId = Number(useParams().examAttemptId)
  const navigate = useNavigate()

  const {
    allQuestionsAnswered,
    basicCourseData,
    currentExamQuestion,
    isExamStarted,
    resumeExam,
    submitAnswer,
    timeIsUp
  } = useContext(ParticipantCourseContext)

  const questionWeight = currentExamQuestion?.questionWeight

  const isTryingToResumeExam = !currentExamQuestion && !basicCourseData

  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([])
  const [currentlySubmitting, setCurrentlySubmitting] = useState<boolean>(false)

  const [questionImageLoaded, setQuestionImageLoaded] = useState<boolean>(false)
  const [answerImageLoaded, setAnswerImageLoaded] = useState<{ [key: number]: boolean }>({})

  const intl = useIntl()

  useEffect(() => {
    setSelectedAnswers([])
    setQuestionImageLoaded(false)
  }, [currentExamQuestion])

  useEffect(() => {
    if (isTryingToResumeExam) {
      resumeExam(examAttemptId)
    }
  }, [isTryingToResumeExam])

  useEffect(() => {
    if (isExamStarted && allQuestionsAnswered) {
      navigate(`/exam/results/${examAttemptId}`, { replace: true })
    }
  }, [isExamStarted, allQuestionsAnswered])

  const handleSubmitQuestion = async () => {
    if (!selectedAnswers.length) {
      notification.error({
        message: intl.formatMessage({ id: 'notification.error' }),
        description: 'Bitte wählen Sie mindestens eine Antwort aus.',
        placement: 'bottomRight',
      })

      return
    }

    setCurrentlySubmitting(true)
    await submitAnswer(selectedAnswers)
    setCurrentlySubmitting(false)
  }

  const renderImageByUrl = (type: ImageType, answerId?: number) => {
    if (type === ImageType.QUESTION) {
      return (
        questionImageLoaded && (
          <>
            <FlexRow>
              <StyledImage
                src={`${imageUrl}/${basicCourseData?.courseId}/questions/${currentExamQuestion?.id}/question.jpg`}
                //src={`${imageUrl}/435/questions/234/question.jpg`} // debugging purposes, delete me soon
                onLoad={() => setQuestionImageLoaded(true)}
                onError={() => setQuestionImageLoaded(false)}
              />
            </FlexRow>
            <Divider style={{ width: '50%' }} />
          </>
        )
      )
    }

    if (type === ImageType.ANSWER && answerId) {
      return (
        <FlexRow>
          <StyledImage
            src={`${imageUrl}/${basicCourseData?.courseId}/questions/${currentExamQuestion?.id}/answers/${answerId}/answer.jpg`}
            //src={`${imageUrl}/435/questions/234/answers/777/answer.jpg`} // debugging purposes, delete me soon
            onLoad={() => setAnswerImageLoaded(prevState => ({ ...prevState, [answerId]: true }))}
            onError={() => setAnswerImageLoaded(prevState => ({ ...prevState, [answerId]: false }))}
            style={{ display: answerImageLoaded[answerId] === false ? 'none' : 'block' }}
          />
        </FlexRow>
      )
    }

    return null
  }

  const handleAnswerSelection = (answerId: number) => {
    if (selectedAnswers.includes(answerId)) {
      setSelectedAnswers(selectedAnswers.filter(id => id !== answerId))
    } else {
      setSelectedAnswers([...selectedAnswers, answerId])
    }
  }

  return (
    <>
      <ParticipantNavbar />
      <FlexRow alignItems='center' justifyContent='center' margin='1em 0' flexWrap>
        <Typography.Title level={3} style={{ marginRight: '1em' }}>
          <div dangerouslySetInnerHTML={{ __html: currentExamQuestion?.text ?? '' }} />
        </Typography.Title>
        <Typography.Text>
          {
            questionWeight
              ? `${currentExamQuestion?.questionWeight} Punkt(e)`
              : ''
          }
        </Typography.Text>
      </FlexRow>
      {renderImageByUrl(ImageType.QUESTION)}
      <FlexRow justifyContent='center' margin='1em 0 0 0'>
        <FlexColumnCentered>
          {currentExamQuestion?.answers.map(answer => (
            <>
              <StyledCheckboxRow key={answer.id}>
                <StyledCheckbox
                  checked={selectedAnswers.includes(answer.id)}
                  onChange={() => handleAnswerSelection(answer.id)}
                >
                  <AnswerText>
                    <div dangerouslySetInnerHTML={{ __html: answer?.text || '' }} />
                  </AnswerText>
                </StyledCheckbox>
              </StyledCheckboxRow>
              {renderImageByUrl(ImageType.ANSWER, answer.id)}
            </>
          ))}
        </FlexColumnCentered>
      </FlexRow>
      <FlexRow width='80%' margin='2em auto' justifyContent='flex-end'>
        <Button
          type="primary"
          onClick={() => handleSubmitQuestion()}
          disabled={currentlySubmitting || !selectedAnswers.length}
          loading={currentlySubmitting}
        >
          {currentlySubmitting
            ? 'Lade...'
            : <FormattedMessage id='generic.next' />}
        </Button>
      </FlexRow>
      {timeIsUp && (
        <TimeIsUpModal
          timeIsUp={timeIsUp}
        />
      )}
    </>
  )
}

export default Exam

const StyledCheckboxRow = styled(FlexRow)`
  justify-content: flex-start;
  align-items: center;
  margin: 0.5em 0;
`

const StyledCheckbox = styled(Checkbox)`
  margin: 0.5em;
`

const FlexColumnCentered = styled(FlexColumn)`
  max-width: 40%;
  width: auto;
  align-items: flex-start;
`

const AnswerText = styled(Typography.Text)`
  font-size: 1.1em;
`

const StyledImage = styled.img`
  width: 40%;
  height: auto;
  margin-left: 1em;
`
